import React, { memo } from "react";

const SocialLink = memo(({ href, icon, label, hoverBg }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`bg-gray-800 p-3 rounded-full hover:${hoverBg} transition-all duration-300 transform hover:scale-110`}
    aria-label={label}
  >
    <img src={icon} alt="" className="w-5 h-5" />
  </a>
));

const QuickLink = memo(({ href, text }) => (
  <li>
    <a
      href={href}
      className="hover:text-green-400 transition-colors duration-300 flex items-center group"
    >
      <span className="mr-2 transform group-hover:translate-x-1 transition-transform">›</span>
      {text}
    </a>
  </li>
));

const ContactItem = memo(({ icon, children }) => (
  <li className="flex items-center hover:text-gray-200 transition-colors">
    {icon}
    {children}
  </li>
));

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const socialLinks = [
    { href: "https://facebook.com", icon: "./assets/images/icons/facebook.svg", label: "Facebook", hoverBg: "bg-blue-600" },
    { href: "https://twitter.com", icon: "./assets/images/icons/x.svg", label: "Twitter", hoverBg: "bg-blue-400" },
    { href: "https://instagram.com", icon: "./assets/images/icons/insta.svg", label: "Instagram", hoverBg: "bg-pink-600" },
    { href: "https://linkedin.com", icon: "./assets/images/icons/linkdin.svg", label: "LinkedIn", hoverBg: "bg-blue-700" }
  ];

  const quickLinks = [
    { href: "/", text: "Home" },
    { href: "/about", text: "About" },
    { href: "/service", text: "Services" },
    { href: "/product", text: "Products" },
    { href: "/contact", text: "Contact" }
  ];

  return (
    <footer className="bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Company Info & About Us */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex flex-col items-start mb-6">
              <img 
                src="./logo.svg" 
                alt="Palnet Industrial Trading PLC" 
                className="w-40 h-auto mb-5 hover:opacity-90 transition-opacity"
              />
              <div>
                <p className="text-green-400 font-medium italic mb-3 text-lg">
                  "Growing through modern agriculture"
                </p>
              </div>
            </div>
            <p className="text-gray-300 text-sm mb-8 leading-relaxed max-w-sm text-left hover:text-gray-200 transition-colors">
              We are a leading company in Ethiopia dedicated to advancing
              agricultural innovation and sustainable growth. We aim to expand our product portfolio, strengthen our global network of partners, and continue to invest in sustainable and innovative agricultural practices.
            </p>
            <div className="flex space-x-5 mt-4">
              {socialLinks.map((link) => (
                <SocialLink key={link.label} {...link} />
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="mt-6 md:mt-0">
            <h4 className="text-xl font-semibold mb-6 relative text-left after:content-[''] after:absolute after:w-12 after:h-1 after:bg-green-500 after:left-0 after:bottom-[-10px] after:rounded-full">
              Quick Links
            </h4>
            <ul className="space-y-4 text-gray-300">
              {quickLinks.map((link) => (
                <QuickLink key={link.href} {...link} />
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="mt-6 md:mt-0">
            <h4 className="text-xl font-semibold mb-6 relative text-left after:content-[''] after:absolute after:w-12 after:h-1 after:bg-green-500 after:left-0 after:bottom-[-10px] after:rounded-full">
              Contact Us
            </h4>
            <ul className="space-y-4 text-gray-300">
              <ContactItem
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                }
              >
                Ethiopia, Addis Ababa
              </ContactItem>
              {["banteyrga@gmail.com", "hello@palnet.et"].map((email) => (
                <ContactItem
                  key={email}
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  }
                >
                  <a href={`mailto:${email}`} className="hover:text-green-400 transition-colors duration-300">
                    {email}
                  </a>
                </ContactItem>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-16 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm hover:text-gray-300 transition-colors">
              © {currentYear} Palnet Industrial Trading PLC. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default memo(Footer);